import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { useDialog } from "primevue/usedialog";
import { useToast } from "primevue/usetoast";
import { type Ref, computed, ref, toValue } from "vue";
import { useRoute, useRouter } from "vue-router";

import DefinitionGeneratorForm from "@/components/Process/DefinitionGeneratorForm.vue";
import { useEditStore } from "@/composables/edit-manager";
import {
    useProcessManager,
    useProcessStore,
} from "@/composables/process-manager";
import { type BpmnArtefact } from "@/lib/artefacts";

export const useGenerateBpmnDialog = ({
    artefact,
}: {
    artefact: Ref<BpmnArtefact | undefined>;
}) => {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const dialog = useDialog();
    const dialogRef = ref<DynamicDialogInstance & { visible?: boolean }>();
    const isVisible = computed(() => Boolean(dialogRef.value?.visible));
    const { generateProcessDefinitionXml, updateProcess } = useProcessStore();
    const { startEditing } = useEditStore();
    const { isAnyGenerating, process } = useProcessManager();

    /**
     * Generate the process definition.
     */
    const generateProcessDefinition = async (description: string) => {
        if (!description || !toValue(artefact) || !process.value) {
            return;
        }

        const targetProcessId = process.value.identity;

        // Navigate to the definition and start editing.
        await router.push({
            name: "processDefinitionPage",
            params: route.params,
        });
        startEditing();

        // Run generation in the background without blocking the dialog
        generateProcessDefinitionXml(description)
            .then((xml) => {
                if (!xml) {
                    toast.add({
                        severity: "error",
                        summary: "No XML content generated.",
                    });
                    return;
                }

                // Backend already saved the XML directly to the definition artefact,
                // so we just trigger a refresh of the process (which includes definition).
                // We only do this if the user hasn't navigated to another process in the meantime.
                if (
                    process.value &&
                    process.value.identity === targetProcessId
                ) {
                    updateProcess(true);
                }

                toast.add({
                    severity: "success",
                    summary: "BPMN generation complete",
                    life: 3000,
                });
                console.log(
                    "Process Definition Generation - XML mode completed"
                );
            })
            .catch((error) => {
                console.error("Error generating BPMN XML directly:", error);
                toast.add({
                    severity: "error",
                    summary: `Could not generate BPMN XML: ${error}`,
                });
            });
    };

    const open = () => {
        if (isAnyGenerating.value) {
            toast.add({
                severity: "warn",
                summary: "Generation in progress",
                detail: "Please wait for the current generation to complete before starting a new one.",
                life: 5000,
            });
            return;
        }
        dialog.open(DefinitionGeneratorForm, {
            props: {
                header: "Generate a first draft",
                style: {
                    maxWidth: "600px",
                    width: "80%",
                },
                modal: true,
                maximizable: false,
                draggable: false,
            },
            data: {
                generateCallback: generateProcessDefinition,
                getIsGenerating: () => false, // Dialog no longer handles generating state
            },
        });
    };

    return { open, isVisible };
};
